import { useMemo } from 'react';
import { EntityGet } from 'data/firestore/types';
import { Todo } from 'data/firestore/agent/todo/types';
import TodoItem from './TodoItem';

interface TodoListProps {
    todos: EntityGet<Todo>[];
    onStatusToggle: (todoId: string, newStatus: string) => void;
    onCancel: (todoId: string) => void;
    onUpdate: (todoId: string, payload: {
        title?: string;
        description?: string;
        status?: 'pending' | 'in_progress' | 'cancelled' | 'done';
        priority?: 'low' | 'medium' | 'high';
        assignedTo?: any | null;
        dueDate?: string | null;
        metadata?: Record<string, unknown>;
    }) => Promise<void>;
    loadingTodoIds: Set<string>;
    agentId: string;
}

const getDueDateTimestamp = (dueDate?: Date | string | null) => {
    if (!dueDate) {
        return Number.POSITIVE_INFINITY;
    }

    const parsedDate = dueDate instanceof Date ? dueDate : new Date(dueDate);
    const timestamp = parsedDate.getTime();

    return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

const compareTodos = (a: EntityGet<Todo>, b: EntityGet<Todo>) => {
    const dueDateDifference = getDueDateTimestamp(a.dueDate as Date | string | null)
        - getDueDateTimestamp(b.dueDate as Date | string | null);
    if (dueDateDifference !== 0) {
        return dueDateDifference;
    }

    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
};

const TodoList = ({
    todos,
    onStatusToggle,
    onCancel,
    onUpdate,
    loadingTodoIds,
    agentId,
}: TodoListProps) => {
    // Organize todos into a hierarchical structure. If a parent is missing,
    // render that todo at root level to avoid silently hiding actionable items.
    const { rootTodos, childrenMap } = useMemo(() => {
        const root: EntityGet<Todo>[] = [];
        const childMap: Record<string, EntityGet<Todo>[]> = {};
        const todoById = todos.reduce(
            (acc, todo) => {
                acc[todo.id] = todo;
                return acc;
            },
            {} as Record<string, EntityGet<Todo>>,
        );

        todos.forEach((todo) => {
            const hasParent = Boolean(todo.parentTodoId);
            const parentExists = hasParent && Boolean(todoById[todo.parentTodoId as string]);

            if (!hasParent || !parentExists) {
                root.push(todo);
            } else {
                const parentTodoId = todo.parentTodoId as string;
                if (!childMap[parentTodoId]) {
                    childMap[parentTodoId] = [];
                }
                childMap[parentTodoId].push(todo);
            }
        });

        root.sort(compareTodos);
        Object.keys(childMap).forEach((parentTodoId) => {
            childMap[parentTodoId].sort(compareTodos);
        });

        return { rootTodos: root, childrenMap: childMap };
    }, [todos]);

    return (
        <>
            {rootTodos.map((todo) => (
                <TodoItem
                    key={todo.id}
                    todo={todo}
                    childTodos={childrenMap[todo.id] || []}
                    onStatusToggle={onStatusToggle}
                    onCancel={onCancel}
                    onUpdate={onUpdate}
                    loadingTodoIds={loadingTodoIds}
                    agentId={agentId}
                />
            ))}
        </>
    );
};

export default TodoList;
